import { ref, shallowRef, defineAsyncComponent, type Component } from 'vue';
import logger from '$utils/logger';
import { templateExists as registryTemplateExists, type TemplateType } from '../templates/registry';

/**
 * Registry de Page templates disponibles
 * Cada template se carga dinámicamente bajo demanda (lazy loading)
 */
const pageTemplateRegistry: Record<string, () => Promise<Component>> = {
    default: () => import('../templates/default/PageTemplate.vue'),
    viaggio: () => import('../templates/viaggio/PageTemplate.vue')
};

/**
 * Registry de Builder templates disponibles
 * Cada template se carga dinámicamente bajo demanda (lazy loading)
 */
const builderTemplateRegistry: Record<string, () => Promise<Component>> = {
    default: () => import('../templates/default/BuilderTemplate.vue'),
    viaggio: () => import('../templates/viaggio/BuilderTemplate.vue')
};

/**
 * Composable para cargar templates dinámicamente
 *
 * Gestiona el lazy loading de templates usando defineAsyncComponent de Vue 3.
 * Los templates son componentes Vue que reciben props y emiten eventos estándar.
 */
export function useTemplateLoader() {
    // Use shallowRef for components to avoid Vue warning about reactive components
    const currentTemplate = shallowRef<Component | null>(null);
    const isLoadingTemplate = ref(false);
    const templateError = ref<string | null>(null);
    const loadedTemplateName = ref<string>('');

    /**
     * Función genérica para cargar un template
     *
     * @param templateName - Nombre del template a cargar (ej: 'default', 'viaggio')
     * @param type - Tipo de template ('page' | 'builder')
     * @param registry - Registry de templates a usar
     * @returns true si la carga fue exitosa, false si hubo error
     */
    const loadTemplate = async (
        templateName: string,
        type: TemplateType,
        registry: Record<string, () => Promise<Component>>
    ): Promise<boolean> => {
        if (!templateName) {
            templateName = 'default';
        }

        // Si ya tenemos este template cargado, no hacer nada
        if (loadedTemplateName.value === templateName && currentTemplate.value) {
            logger.info(`useTemplateLoader: ${type} template '${templateName}' already loaded`);
            return true;
        }

        isLoadingTemplate.value = true;
        templateError.value = null;

        try {
            logger.info(`useTemplateLoader: Loading ${type} template '${templateName}'`);

            // Verificar que el template existe en el registry
            const templateLoader = registry[templateName];

            if (!templateLoader) {
                logger.warn(`useTemplateLoader: ${type} template '${templateName}' not found, falling back to 'default'`);
                templateName = 'default';
            }

            // Crear el componente async con defineAsyncComponent
            currentTemplate.value = defineAsyncComponent({
                loader: registry[templateName],
                loadingComponent: undefined, // Podríamos agregar un spinner aquí
                errorComponent: undefined, // Podríamos agregar un componente de error
                delay: 0,
                timeout: 10000
            });

            loadedTemplateName.value = templateName;
            logger.info(`useTemplateLoader: ${type} template '${templateName}' loaded successfully`);

            return true;
        } catch (err: any) {
            templateError.value = err.message || 'Unknown error loading template';
            logger.error(`useTemplateLoader: Error loading ${type} template '${templateName}':`, err);

            // Intentar cargar el template default como fallback
            if (templateName !== 'default') {
                logger.info(`useTemplateLoader: Attempting to load default ${type} template as fallback`);
                return loadTemplate('default', type, registry);
            }

            return false;
        } finally {
            isLoadingTemplate.value = false;
        }
    };

    /**
     * Carga un Page template por nombre
     *
     * @param templateName - Nombre del template a cargar (ej: 'default', 'viaggio')
     * @returns true si la carga fue exitosa, false si hubo error
     */
    const loadPageTemplate = async (templateName: string): Promise<boolean> => {
        return loadTemplate(templateName, 'page', pageTemplateRegistry);
    };

    /**
     * Carga un Builder template por nombre
     *
     * @param templateName - Nombre del template a cargar (ej: 'default', 'viaggio')
     * @returns true si la carga fue exitosa, false si hubo error
     */
    const loadBuilderTemplate = async (templateName: string): Promise<boolean> => {
        return loadTemplate(templateName, 'builder', builderTemplateRegistry);
    };

    /**
     * Obtiene la lista de templates disponibles
     * @param type - Tipo de template ('page' | 'builder'), default: 'page'
     */
    const getAvailableTemplates = (type: TemplateType = 'page'): string[] => {
        const registry = type === 'builder' ? builderTemplateRegistry : pageTemplateRegistry;
        return Object.keys(registry);
    };

    /**
     * Verifica si un template existe
     * @param templateName - Nombre del template
     * @param type - Tipo de template ('page' | 'builder'), default: 'page'
     */
    const templateExists = (templateName: string, type: TemplateType = 'page'): boolean => {
        return registryTemplateExists(templateName, type);
    };

    return {
        currentTemplate,
        isLoadingTemplate,
        templateError,
        loadedTemplateName,
        loadPageTemplate,
        loadBuilderTemplate,
        getAvailableTemplates,
        templateExists
    };
}
